import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchLanguage, createLanguage, updateLanguage } from "@/api/languages";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import { Language } from "@/types";

// Form validation schema
const languageSchema = z.object({
  code: z
    .string()
    .min(2, { message: "Language code must be at least 2 characters" })
    .max(10, { message: "Language code must be less than 10 characters" })
    .regex(/^[a-z-]+$/, {
      message: "Language code must contain only lowercase letters and hyphens",
    }),
  name: z
    .string()
    .min(2, { message: "Language name must be at least 2 characters" })
    .max(50, { message: "Language name must be less than 50 characters" }),
});

type FormData = z.infer<typeof languageSchema>;

const LanguageForm = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!code;

  // Form state
  const [formData, setFormData] = useState<FormData>({
    code: "",
    name: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const { toast } = useToast();

  // Fetch language data if in edit mode
  const { data: language, isLoading } = useQuery({
    queryKey: ["language", code],
    queryFn: () => fetchLanguage(code as string),
    enabled: isEditMode,
  });

  // Populate form with language data when available
  useEffect(() => {
    if (isEditMode && language) {
      setFormData({
        code: language.code,
        name: language.name,
      });
    }
  }, [isEditMode, language]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear the error for this field when the user types
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    try {
      languageSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      if (isEditMode && code) {
        // Update existing language
        await updateLanguage(code, formData.name);
        toast({
          title: "Language updated",
          description: `"${formData.name}" has been updated successfully.`,
        });
      } else {
        // Create new language
        await createLanguage(formData.code, formData.name);
        toast({
          title: "Language created",
          description: `"${formData.name}" has been created successfully.`,
        });
      }
      navigate("/admin/system/languages");
    } catch (error: unknown) {
      console.error("Error saving language:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An error occurred while saving the language";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && isEditMode) {
    return (
      <AdminLayout title={isEditMode ? "Edit Language" : "New Language"}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditMode ? "Edit Language" : "New Language"}>
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Languages
        </Button>
      </div>

      <Card className="max-w-lg mx-auto">
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <CardTitle>
              {isEditMode ? "Edit Language" : "Add New Language"}
            </CardTitle>
            <CardDescription>
              {isEditMode
                ? "Update the language information below"
                : "Enter the details for the new language"}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="code"
                className={errors.code ? "text-destructive" : ""}
              >
                Language Code <span className="text-destructive">*</span>
              </Label>
              <Input
                id="code"
                name="code"
                placeholder="en"
                value={formData.code}
                onChange={handleChange}
                disabled={isEditMode}
                className={errors.code ? "border-destructive" : ""}
              />
              {isEditMode && (
                <p className="text-sm text-muted-foreground">
                  Language code cannot be changed after creation
                </p>
              )}
              {errors.code && (
                <p className="text-destructive text-sm">{errors.code}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="name"
                className={errors.name ? "text-destructive" : ""}
              >
                Language Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                placeholder="English"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? "border-destructive" : ""}
              />
              {errors.name && (
                <p className="text-destructive text-sm">{errors.name}</p>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {isEditMode ? "Update Language" : "Create Language"}
                </>
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </AdminLayout>
  );
};

export default LanguageForm;
